<template>
  <div>
    <b-modal ref="SalesContractPositionModal" :title="modalTitle" :no-close-on-backdrop="true">
      <div slot="modal-title">
        <div class="d-flex">
          <h5 class="modal-title">{{modalTitle}}</h5>
        </div>
      </div>
      <div @click="modalClick">
        <b-row>
          <b-col>
            <label>{{$t('modal_dialogs.sales_contract_position.name')}}</label>
            <vue-suggest
              :list="productNames"
              :filter-by-query="true"
              @select="selectProductName" :class="{ required: !salesContractItemData.name}"
              mode="input"
            >
              <input class="form-control" v-model="salesContractItemData.name" id="auto-complete">
            </vue-suggest>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('modal_dialogs.sales_contract_position.product')}}</label>
            <div class="d-flex">
              <multiselect v-model="salesContractItemData.product" label="name" track-by="name"
                           placeholder="Search product" open-direction="bottom" :options="products"
                           :multiple="false" :searchable="true" :loading="isLoadingProducts"
                           :internal-search="false"
                           :close-on-select="true" :max-height="200"
                           :hide-selected="false" @search-change="asyncProductsSearch($event, 'product')">
                <span slot="noResult">{{$t('modal_dialogs.sales_contract_position.no_result_text')}}</span>
              </multiselect>
              <div @click="applySalesProductFromProduct" class="rotate-90 share-icon-wrapper">
                <icon name="share" class="share-icon"></icon>
              </div>
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('modal_dialogs.sales_contract_position.sales_product')}}</label>
            <multiselect v-model="salesContractItemData.sale_product" label="name" track-by="name"
                         placeholder="Search sales product" open-direction="bottom" :options="saleProducts"
                         :multiple="false" :searchable="true" :loading="isLoadingSalesProducts"
                         :internal-search="false"
                         :close-on-select="true" :max-height="200"
                         :hide-selected="false"
                         @search-change="asyncProductsSearch($event, 'sale_product')">
              <span slot="noResult">{{$t('modal_dialogs.sales_contract_position.no_result_text')}}</span>
            </multiselect>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('modal_dialogs.sales_contract_position.package_size')}}</label>
            <input type="number" class="form-control" v-model="salesContractItemData.pack_size"/>
          </b-col>
          <b-col>
            <label>{{$t('modal_dialogs.sales_contract_position.article')}}</label>
            <input class="form-control" v-model="salesContractItemData.article_no"/>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('modal_dialogs.sales_contract_position.package_amount')}}</label>
            <input type="number" class="form-control" v-model="salesContractItemData.pack_count"/>
          </b-col>
          <b-col cols="2" class="padding-right-0">
            <label>{{$t('modal_dialogs.sales_contract_position.price')}}</label>
            <input class="form-control" type="number" v-model="salesContractItemData.stock_price" step="any"
                   :class="{ required: salesContractItemData.stock_price === ''}"
                   @input="convertStockPriceToSalePrice"/>
          </b-col>
          <b-col cols="2" class="padding-0">
            <label style="padding-left: 10px;">{{$t('modal_dialogs.sales_contract_position.sale')}}</label>
            <div class="d-flex">
              <span class="align-self-bottom symbol">=</span>
              <input class="form-control" type="number" v-model="salesContractItemData.sale_price"
                     step="any"
                     :class="{ required: salesContractItemData.sale_price === ''}" style="width: 80%"/>
              <span class="align-self-bottom slash-symbol">/</span>
            </div>
          </b-col>
          <b-col cols="2" class="d-flex padding-left-0" style="padding-bottom: 2px">
            <multiselect class="align-self-bottom" v-model="salesContractItemData.measurement_for_price"
                         :max-height="200" :searchable="true"
                         placeholder="" :show-labels="false"
                         label="name" track-by="name" :options="measurements"
                         :allowEmpty="false"></multiselect>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="3">
            <label>{{$t('modal_dialogs.sales_contract_position.amount')}}</label>
            <input class="form-control" type="number" v-model="salesContractItemData.amount" step="any"
                   :class="{ required: salesContractItemData.amount === ''}"/>
          </b-col>
          <b-col cols="3" class="d-flex">
            <multiselect class="align-self-bottom" v-model="salesContractItemData.measurement_value"
                         :max-height="200" :searchable="true"
                         placeholder="" :show-labels="false"
                         label="name" track-by="name" :options="measurement_values"
                         :allowEmpty="false"></multiselect>
          </b-col>
          <b-col class="d-flex floating-length-wrapper">
            <label class="align-self-bottom">{{$t('modal_dialogs.sales_contract_position.floating_length')}}</label>
            <input class="align-self-bottom" type="checkbox"
                   v-model="salesContractItemData.floating_length">
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('modal_dialogs.sales_contract_position.package_type')}}</label>
            <multiselect class="align-self-bottom" v-model="salesContractItemData.package_type"
                         :max-height="200" :searchable="true"
                         placeholder="" :show-labels="false"
                         label="name" track-by="name" :options="packageTypes"
                         :allowEmpty="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.notes')}}</label>
            <b-form-textarea placeholder="Enter notes" v-model="salesContractItemData.notes">
            </b-form-textarea>
          </b-col>
        </b-row>
        <b-row v-if="salesContractItemData.package_type && salesContractItemData.package_type.name === 'Bundles'">
          <b-col cols="6">
            <label>{{$t('modal_dialogs.sales_contract_position.bundle_size')}}</label>
            <input class="form-control" type="number" v-model="salesContractItemData.bundle_size">
          </b-col>
        </b-row>
      </div>
      <div slot="modal-footer" class="w-100 text-center">
        <b-btn size="sm" variant="success" @click="saveSalesContractItem">
          {{$t('buttons.common.save')}}
        </b-btn>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import Multiselect from 'vue-multiselect';
  import { mapGetters } from 'vuex';
  import {
    handlerResponse
  } from '@/helpers/commonHelpers';
  import VueSuggest from 'vue-simple-suggest'
  import 'vue-simple-suggest/dist/styles.css'

  export default {
    props: ['id'],
    components: {
      Multiselect,
      VueSuggest
    },
    data() {
      return {
        measurements: [
          {name: 'm3', key: 'm3'},
          {name: 'm2', key: 'm2'},
          {name: 'lm', key: 'lm'},
          {name: 'pcs', key: 'pieces'},
          {name: 'bdl', key: 'bundles'}
        ],
        measurement_values: [],
        requiredFields: ['name', 'amount', 'price'],
        isLoadingProducts: false,
        isLoadingSalesProducts: false,
        products: [],
        saleProducts: [],
        productNames: [],
        isNewSalesContractItem: true,
        salesContractItemData: {},
        salesContractItemDataInitial: {
          product: null,
          sale_product: null,
          name: '',
          article_no: '',
          pack_size: null,
          pack_count: null,
          amount: 0,
          measurement_value: {name: 'm3', key: 'm3'},
          package_type: {},
          bundle_size: 1,
          stock_price: 0,
          sale_price: 0,
          measurement_for_price: {name: 'm3', key: 'm3'},
          floating_length: false,
          notes: ''
        }
      }
    },
    methods: {
      asyncProductsSearch(query, type) {
        if (query) {
          if (type === 'product') {
            this.isLoadingProducts = true;
          } else {
            this.isLoadingSalesProducts = true;
          }
          const params = {
            limit: 50,
            offset: 0,
            text: query,
            product_sizes: true
          };
          this.$http.get(`woodware/list/search_for_product_short_view/`, {params}).then(handlerResponse).then(response => {
            if (type === 'product') {
              this.products = response.results;
              this.isLoadingProducts = false;
            } else {
              this.saleProducts = response.results;
              this.isLoadingSalesProducts = false;
            }
          })
        }
      },
      saveSalesContractItem() {
        if (this.validFormData) {
          const savedData = {
            ...this.salesContractItemData, ...
              {
                product: this.salesContractItemData.product && this.salesContractItemData.product.id,
                sale_product: this.salesContractItemData.sale_product && this.salesContractItemData.sale_product.id,
                measurement_for_price: this.salesContractItemData.measurement_for_price.key,
                measurement_value: this.salesContractItemData.measurement_value.key,
                package_type: this.salesContractItemData.package_type.id,
                pack_size: this.salesContractItemData.pack_size === '' ? null : this.salesContractItemData.pack_size,
                pack_count: this.salesContractItemData.pack_count === '' ? null : this.salesContractItemData.pack_count
              }
          };
          if (this.isNewSalesContractItem) {
            this.$http.post(`sales/sales_contracts/${this.id}/sales_contract_items/`, savedData).then(handlerResponse).then(() => {
              this.$refs['SalesContractPositionModal'].hide();
              this.$store.dispatch('salesContract/loadSalesContractItems', {id: this.id});
            }).catch((error) => {
              console.log(error)
            });
          } else {
            const salesContractItemId = this.salesContractItemData.id;
            this.$http.put(`sales/sales_contracts/${this.id}/sales_contract_items/${salesContractItemId}/`, savedData).then(handlerResponse).then(() => {
              this.$refs['SalesContractPositionModal'].hide();
              this.$store.dispatch('salesContract/loadSalesContractItems', {id: this.id});
            }).catch((error) => {
              console.log(error)
            });
          }
        } else {
          this.$swal.fire('', 'Fill required fields', 'error')
        }
      },
      selectProductName(name) {
        const params = {name};
        if (name) {
          this.$http.get(`sales/last_sales_contract_item_by_name/`, {params}).then(handlerResponse).then((response) => {
            this.salesContractItemData = {...response};
          });
        }
      },
      applySalesProductFromProduct() {
        this.salesContractItemData.sale_product = this.salesContractItemData.product;
        this.convertStockPriceToSalePrice();
      },
      convertStockPriceToSalePrice() {
        if (this.salesContractItemData.product && this.salesContractItemData.sale_product) {
          const {width: productWidth, thickness: productThickness} = this.salesContractItemData.product;
          const {width: saleProductWidth, thickness: saleProductThickness} = this.salesContractItemData.sale_product;
          const productSizes = productWidth * productThickness,
            salesProductSizes = saleProductWidth * saleProductThickness;
          this.salesContractItemData.sale_price = (this.salesContractItemData.stock_price * productSizes / salesProductSizes).toFixed(2);
        }
      },
      modalClick(event) {
        const clickedElement = event.target;
        if (clickedElement.id !== 'auto-complete') {
          const autoCompleteElem = document.getElementById('auto-complete');
          autoCompleteElem.blur();
        }
      }
    },
    computed: {
      ...mapGetters('generic', {
        packageTypes: 'packageTypes',
      }),
      modalTitle() {
        return `${this.$i18n.t('modal_dialogs.sales_contract_position.sales_contract_position')}`
      },
      validFormData() {
        return this.requiredFields.filter((item) => {
          return this.salesContractItemData[item] !== '' && this.salesContractItemData[item] !== null;
        }).length === this.requiredFields.length;
      }
    },
    mounted() {
      this.measurement_values = this.measurements.filter(item => item.key !== 'bundles');
      this.salesContractItemData = {...this.salesContractItemDataInitial};
      this.$root.$on('openSalesContractPositionModal', (data) => {
        (async () => {
          await this.$store.cache.dispatch('generic/loadPackageTypes');
          this.productNames = await this.$http.get(`sales/sales_contract_items_names/`).then(handlerResponse);
          this.products = [];
          this.salesContractItemData = {...this.salesContractItemDataInitial};
          this.isNewSalesContractItem = true;
          if (this.$refs['SalesContractPositionModal']) {
            this.$refs['SalesContractPositionModal'].show();
            this.salesContractItemData.package_type = this.packageTypes[0];
            if (data.id) {
              this.isNewSalesContractItem = false;
              this.salesContractItemData = {...data};
            }
          }
        })();
      })
    },
    beforeDestroy() {
      this.$root.$off('openSalesContractPositionModal');
    },
  }
</script>

<style scoped>
  .floating-length-wrapper {
    padding-bottom: 7px;
  }

  .floating-length-wrapper input[type='checkbox'] {
    margin-left: 10px;
  }

</style>



// WEBPACK FOOTER //
// src/components/modals/SalesContractPositionModal.vue